import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subscription } from 'rxjs';
import { Account, AccountMember, NewsletterFrequency } from 'src/app/models/account';
import { AccountService } from 'src/app/services/account.service';
import { AuthService } from 'src/app/services/auth.service';
import { I18nService } from 'src/app/services/i18n.service';
import { Theme, ThemeService } from 'src/app/services/theme.service';
import { LanguageCode } from 'src/app/i18n/languages';
import { DialogContentLogin } from '../header-sticky/login-dialog/dialog-content-login';
import {
  DEFAULT_PREFERENCES, defaultNotificationSetting, HOME_PAGE_OPTIONS, NEWSLETTER_OPTIONS, NOTIFICATION_GROUPS,
  PREFERENCE_GROUPS, SETTINGS_SECTIONS, SettingsSectionId,
} from './settings-options';

type EditableField = 'pseudo' | 'description' | 'email' | 'password' | 'delete';

const AVATAR_SIZE = 256;
const DEFAULT_AVATAR = 'assets/userAvatar.svg';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SettingsComponent implements OnInit, OnDestroy {

  readonly sections = SETTINGS_SECTIONS;
  readonly preferenceGroups = PREFERENCE_GROUPS;
  readonly notificationGroups = NOTIFICATION_GROUPS;
  readonly homePageOptions = HOME_PAGE_OPTIONS;
  readonly newsletterOptions = NEWSLETTER_OPTIONS;
  readonly defaultAvatar = DEFAULT_AVATAR;

  section: SettingsSectionId = 'profil';
  isLoggedIn = false;
  loading = false;
  loadError = '';
  account: Account | null = null;

  editing: EditableField | null = null;
  saving = false;
  formError = '';
  pseudoInput = '';
  descriptionInput = '';
  emailInput = '';
  passwordInput = '';
  newPasswordInput = '';
  confirmPasswordInput = '';

  newsletterFrequency: NewsletterFrequency = 'daily';
  selectedFollowing = new Set<string>();
  selectedIgnored = new Set<string>();

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private accountService: AccountService,
    public i18n: I18nService,
    private themeService: ThemeService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(this.route.paramMap.subscribe(params => {
      const section = params.get('section') as SettingsSectionId;
      if (!this.sections.some(s => s.id === section)) {
        this.router.navigate(['/settings', 'profil'], { replaceUrl: true });
        return;
      }
      this.section = section;
      this.cancelEdit();
    }));

    this.subscriptions.add(this.accountService.account$.subscribe(account => {
      this.account = account;
      if (account) {
        this.newsletterFrequency = account.newsletter.frequency;
        // Les membres retirés de la liste ne doivent pas rester sélectionnés
        this.selectedFollowing = this.keepExisting(this.selectedFollowing, account.following);
        this.selectedIgnored = this.keepExisting(this.selectedIgnored, account.ignored);
      }
    }));

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.subscriptions.add(this.authService.loggedIn$.subscribe(loggedIn => {
      const wasLoggedIn = this.isLoggedIn;
      this.isLoggedIn = loggedIn;
      if (loggedIn && !wasLoggedIn) {
        this.loadAccount();
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadAccount(): void {
    this.loading = true;
    this.loadError = '';
    this.accountService.load().subscribe({
      next: () => this.loading = false,
      error: err => {
        this.loading = false;
        this.loadError = err || this.i18n.t('settings.loadError');
      }
    });
  }

  goBack(): void {
    this.location.back();
  }

  openLogin(): void {
    this.dialog.open(DialogContentLogin, { width: '550px' });
  }

  // ---------- Profil ----------

  get avatarSrc(): string {
    return this.account?.avatar || this.defaultAvatar;
  }

  get canChangePseudo(): boolean {
    const next = this.account?.nextPseudoChangeAt;
    return !next || new Date(next) <= new Date();
  }

  startEdit(field: EditableField): void {
    this.editing = field;
    this.formError = '';
    this.pseudoInput = this.account?.pseudo || '';
    this.descriptionInput = this.account?.description || '';
    this.emailInput = '';
    this.passwordInput = '';
    this.newPasswordInput = '';
    this.confirmPasswordInput = '';
  }

  cancelEdit(): void {
    this.editing = null;
    this.formError = '';
    this.passwordInput = '';
    this.newPasswordInput = '';
    this.confirmPasswordInput = '';
  }

  savePseudo(): void {
    this.submit(this.accountService.updateProfile({ pseudo: this.pseudoInput.trim() }), this.i18n.t('settings.profile.pseudoSaved'));
  }

  saveDescription(): void {
    this.submit(this.accountService.updateProfile({ description: this.descriptionInput }), this.i18n.t('settings.profile.descriptionSaved'));
  }

  saveEmail(): void {
    this.submit(this.accountService.updateEmail(this.emailInput.trim(), this.passwordInput),
      this.i18n.t('settings.profile.emailSaved'));
  }

  savePassword(): void {
    if (this.newPasswordInput !== this.confirmPasswordInput) {
      this.formError = this.i18n.t('common.passwordsMismatch');
      return;
    }
    this.submit(this.accountService.updatePassword(this.passwordInput, this.newPasswordInput), this.i18n.t('settings.profile.passwordSaved'));
  }

  deleteAccount(): void {
    if (!confirm(this.i18n.t('settings.profile.deleteConfirm'))) {
      return;
    }
    this.saving = true;
    this.formError = '';
    this.accountService.deleteAccount(this.passwordInput).subscribe({
      next: () => {
        this.saving = false;
        this.snackBar.open(this.i18n.t('settings.profile.accountDeleted'), this.i18n.t('common.ok'), { duration: 4000 });
        this.router.navigate(['/']);
      },
      error: err => {
        this.saving = false;
        this.formError = err || this.i18n.t('settings.profile.deleteFailed');
      }
    });
  }

  onAvatarSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      this.notify(this.i18n.t('settings.profile.chooseImage'));
      return;
    }
    this.resizeImage(file).then(dataUrl => {
      this.submit(this.accountService.updateAvatar(dataUrl), this.i18n.t('settings.profile.avatarSaved'));
    }).catch(() => this.notify(this.i18n.t('settings.profile.imageUnreadable')));
  }

  removeAvatar(): void {
    this.submit(this.accountService.removeAvatar(), this.i18n.t('settings.profile.avatarRemoved'));
  }

  exportData(): void {
    this.accountService.exportData().subscribe({
      next: data => {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'promodeep-mes-donnees.json';
        link.click();
        URL.revokeObjectURL(url);
      },
      error: err => this.notify(err || this.i18n.t('settings.profile.exportFailed'))
    });
  }

  // ---------- Préférences / notifications ----------

  // '' = langue de l'appareil (aucun choix explicite)
  get languageChoice(): LanguageCode | '' {
    return this.i18n.userChoice || '';
  }

  get deviceLanguageName(): string {
    return this.i18n.languageInfo(this.i18n.deviceLanguage).nativeName;
  }

  setLanguage(code: LanguageCode | ''): void {
    this.i18n.setLanguage(code || null).then(() => {
      // Enregistrée aussi sur le compte pour être retrouvée sur les autres appareils
      if (this.account) {
        this.setPreference('language', code);
      }
    });
  }

  // '' = thème automatique selon l'heure (aucun choix explicite)
  get themeChoice(): Theme | '' {
    return this.themeService.userChoice || '';
  }

  setTheme(theme: Theme | ''): void {
    this.themeService.setTheme(theme || null);
    // Enregistré aussi sur le compte pour être retrouvé sur les autres appareils
    if (this.account) {
      this.setPreference('theme', theme);
    }
  }

  preference(key: string): boolean | string {
    const value = this.account?.preferences?.[key];
    return value === undefined ? DEFAULT_PREFERENCES[key] : value;
  }

  setPreference(key: string, value: boolean | string): void {
    this.accountService.updatePreferences({ [key]: value }).subscribe({
      next: () => this.notify(this.i18n.t('settings.preferences.saved')),
      error: err => this.notify(err || this.i18n.t('settings.saveFailed'))
    });
  }

  notification(key: string): boolean {
    const value = this.account?.notificationSettings?.[key];
    return value === undefined ? defaultNotificationSetting(key) : value;
  }

  setNotification(key: string, value: boolean): void {
    this.accountService.updateNotificationSettings({ [key]: value }).subscribe({
      next: () => this.notify(this.i18n.t('settings.notifications.saved')),
      error: err => this.notify(err || this.i18n.t('settings.saveFailed'))
    });
  }

  // ---------- Abonnements ----------

  updateNewsletter(subscribed: boolean): void {
    this.accountService.updateNewsletter({ subscribed, frequency: this.newsletterFrequency }).subscribe({
      next: () => this.notify(this.i18n.t(subscribed ? 'settings.subscriptions.saved' : 'settings.subscriptions.unsubscribed')),
      error: err => this.notify(err || this.i18n.t('settings.saveFailed'))
    });
  }

  updatePartnerOffers(partnerOffers: boolean): void {
    this.accountService.updateNewsletter({ partnerOffers }).subscribe({
      next: () => this.notify(this.i18n.t(partnerOffers ? 'settings.subscriptions.partnerOffersOn' : 'settings.subscriptions.partnerOffersOff')),
      error: err => this.notify(err || this.i18n.t('settings.saveFailed'))
    });
  }

  // ---------- Membres suivis / ignorés ----------

  toggleSelection(selection: Set<string>, id: string, checked: boolean): void {
    if (checked) {
      selection.add(id);
    } else {
      selection.delete(id);
    }
  }

  selectedIds(selection: Set<string>): string[] {
    return [...selection];
  }

  unfollow(ids: string[]): void {
    this.accountService.unfollow(ids).subscribe({
      next: () => this.notify(this.i18n.t('settings.members.listUpdated')),
      error: err => this.notify(err || this.i18n.t('settings.operationFailed'))
    });
  }

  unignore(ids: string[]): void {
    this.accountService.unignore(ids).subscribe({
      next: () => this.notify(this.i18n.t('settings.members.listUpdated')),
      error: err => this.notify(err || this.i18n.t('settings.operationFailed'))
    });
  }

  memberAvatar(member: AccountMember): string {
    return member.avatar || this.defaultAvatar;
  }

  // ---------- Utilitaires ----------

  private submit(request: Observable<unknown>, successMessage: string): void {
    this.saving = true;
    this.formError = '';
    request.subscribe({
      next: () => {
        this.saving = false;
        this.notify(successMessage);
        this.cancelEdit();
        // Le profil (pseudo, avatar, email) vient de changer côté serveur
        this.accountService.load().subscribe();
      },
      error: err => {
        this.saving = false;
        this.formError = err || this.i18n.t('settings.saveFailed');
        if (!this.editing) {
          this.notify(this.formError);
        }
      }
    });
  }

  private notify(message: string): void {
    this.snackBar.open(message, this.i18n.t('common.ok'), { duration: 3000 });
  }

  private keepExisting(selection: Set<string>, members: AccountMember[]): Set<string> {
    const ids = new Set(members.map(m => m._id));
    return new Set([...selection].filter(id => ids.has(id)));
  }

  // Recadre au centre en carré et réduit à 256x256 (JPEG) pour garder un avatar léger
  private resizeImage(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const image = new Image();
        image.onerror = reject;
        image.onload = () => {
          const side = Math.min(image.width, image.height);
          const canvas = document.createElement('canvas');
          canvas.width = AVATAR_SIZE;
          canvas.height = AVATAR_SIZE;
          const context = canvas.getContext('2d');
          if (!context) {
            reject(new Error('canvas'));
            return;
          }
          context.drawImage(image, (image.width - side) / 2, (image.height - side) / 2, side, side, 0, 0, AVATAR_SIZE, AVATAR_SIZE);
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        };
        image.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    });
  }
}
